// Composition of every per-chain wallet provider so child components
// can `useAccount()` (wagmi), `useWallet()` (sol), `useStarknet()`
// (starknet-react), `useNearWallet()` (custom), etc. without each page
// re-wiring providers.

import type { PropsWithChildren } from "react";
import { useEffect, useMemo, useState } from "react";
import {
  type CreateConfigParameters,
  WagmiProvider,
  createConfig,
  http,
} from "wagmi";
import {
  arbitrumSepolia,
  baseSepolia,
  bscTestnet,
  polygonAmoy,
  sepolia,
  abstractTestnet,
  arbitrum,
  base,
  bsc,
  mainnet as ethMainnet,
  polygon,
  abstract as abstractMainnet,
} from "wagmi/chains";
import { injected } from "wagmi/connectors";
import { ConnectionProvider, WalletProvider as SolWalletProvider } from "@solana/wallet-adapter-react";
import {
  InjectedConnector,
  StarknetConfig,
  publicProvider,
  voyager,
} from "@starknet-react/core";
import { mainnet as snMainnet, sepolia as snSepolia } from "@starknet-react/chains";
import { NearWalletProvider } from "@/wallet/near";
import { BtcWalletProvider } from "@/wallet/btc";
import { useHealth } from "@/hooks/useHealth";
import { chains } from "@/config/chains";

function makeWagmiConfig(network: "mainnet" | "testnet"): ReturnType<typeof createConfig> {
  const cfg = chains(network);
  // The wagmi `chains` tuple and `transports` record must list the
  // exact same IDs; wagmi looks up the right transport by `chain.id`
  // at every `eth_call` / `sendTransaction`. We keep them aligned by
  // building both from `ChainBundle.evm` for the active network.
  if (network === "mainnet") {
    const wagmiChains = [ethMainnet, arbitrum, base, bsc, polygon, abstractMainnet] as const;
    const transports = {
      [ethMainnet.id]: http(cfg.evm.eth.rpcUrl),
      [arbitrum.id]: http(cfg.evm.arb.rpcUrl),
      [base.id]: http(cfg.evm.base.rpcUrl),
      [bsc.id]: http(cfg.evm.bnb.rpcUrl),
      [polygon.id]: http(cfg.evm.pol.rpcUrl),
      [abstractMainnet.id]: http(cfg.evm.abs.rpcUrl),
    } satisfies CreateConfigParameters<typeof wagmiChains>["transports"];
    return createConfig({
      chains: wagmiChains,
      transports,
      connectors: [injected({ shimDisconnect: true })],
      multiInjectedProviderDiscovery: true,
    });
  }
  const wagmiChains = [sepolia, arbitrumSepolia, baseSepolia, bscTestnet, polygonAmoy, abstractTestnet] as const;
  const transports = {
    [sepolia.id]: http(cfg.evm.eth.rpcUrl),
    [arbitrumSepolia.id]: http(cfg.evm.arb.rpcUrl),
    [baseSepolia.id]: http(cfg.evm.base.rpcUrl),
    [bscTestnet.id]: http(cfg.evm.bnb.rpcUrl),
    [polygonAmoy.id]: http(cfg.evm.pol.rpcUrl),
    [abstractTestnet.id]: http(cfg.evm.abs.rpcUrl),
  } satisfies CreateConfigParameters<typeof wagmiChains>["transports"];
  return createConfig({
    chains: wagmiChains,
    transports,
    connectors: [injected({ shimDisconnect: true })],
    multiInjectedProviderDiscovery: true,
  });
}

const STARKNET_CONNECTORS = [
  new InjectedConnector({ options: { id: "argentX", name: "Argent X" } }),
  new InjectedConnector({ options: { id: "braavos", name: "Braavos" } }),
];

export function WalletProviders({ children }: PropsWithChildren) {
  const { network, isLoading, isFetching, data } = useHealth();
  // Latch the network ONCE — the first resolved value from /api/health
  // (or, if that never resolves, the default-network fallback after
  // one tick). After that we never re-create the wagmi / Solana /
  // Starknet configs, which would otherwise unmount the active
  // wallets when the upstream health response flipped from the
  // initial guess to the real value.
  const [latched, setLatched] = useState<"mainnet" | "testnet" | null>(null);
  useEffect(() => {
    if (latched !== null) return;
    if (data) {
      setLatched(network);
      return;
    }
    // If `/api/health` never returns (offline, dev backend down) we
    // still want to render the UI eventually. Fall back to the default
    // network after the first fetch attempt completes.
    if (!isFetching && !isLoading) {
      setLatched(network);
    }
  }, [latched, data, isFetching, isLoading, network]);

  if (latched === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 dark:bg-slate-950 text-sm text-slate-500">
        Loading network configuration…
      </div>
    );
  }

  return <ProviderStack network={latched}>{children}</ProviderStack>;
}

/**
 * The actual provider stack. Lives in its own component so the network
 * decision is locked into a single mount — once `network` is fixed at
 * the boundary, the wagmi / Solana / Starknet configs never recreate
 * for the rest of the session, even if `/api/health` returns a
 * different value on a refetch.
 */
function ProviderStack({
  network,
  children,
}: PropsWithChildren<{ network: "mainnet" | "testnet" }>) {
  const cfg = chains(network);
  const wagmiConfig = useMemo(() => makeWagmiConfig(network), [network]);
  // No legacy `*WalletAdapter` instances — modern Phantom / Solflare /
  // Backpack all register as Wallet Standard wallets, and
  // `<WalletProvider>` auto-discovers them via the standard. Passing the
  // legacy adapters in addition triggers a deprecation warning ("X was
  // registered as a Standard Wallet. The Wallet Adapter for X can be
  // removed from your app.") and double-registers the wallet.
  const solanaWallets = useMemo(() => [], []);
  return (
    <WagmiProvider config={wagmiConfig}>
      <StarknetConfig
        chains={network === "mainnet" ? [snMainnet] : [snSepolia]}
        provider={publicProvider()}
        connectors={STARKNET_CONNECTORS}
        explorer={voyager}
        autoConnect
      >
        <ConnectionProvider endpoint={cfg.solana.rpcUrl}>
          <SolWalletProvider wallets={solanaWallets} autoConnect>
            <NearWalletProvider network={network}>
              <BtcWalletProvider>{children}</BtcWalletProvider>
            </NearWalletProvider>
          </SolWalletProvider>
        </ConnectionProvider>
      </StarknetConfig>
    </WagmiProvider>
  );
}
